<template>
  <div class="overlay" :class="{ visible: internalVisible, shown }">
    <div class="dialog">
      <h3 class="title">Daily Report Summary</h3>
      <div class="content">
        <div class="list-item">
          <div class="item-title">Successful Deliveries:</div>
          <div class="item-content">{{ times.length }}</div>
        </div>
        <div class="list-item">
          <div class="item-title">Total Time:</div>
          <div class="item-content">{{ totalTime }}s</div>
        </div>
        <div class="list-item" v-if="times.length !== 0">
          <div class="item-title">Rating:</div>
          <div class="item-content rating">
            <span v-for="(star, index) in stars" :key="index">{{
              star ? "★" : "☆"
            }}</span>
          </div>
        </div>
        <div class="list-item">
          <div class="item-title">Earnings:</div>
          <div class="item-content">${{ times.length * 5 }}</div>
        </div>
        <div class="list-item">
          <div class="item-title">Tips:</div>
          <div class="item-content">${{ tips }}</div>
        </div>
        <div class="list-item">
          <div class="item-title">Total:</div>
          <div class="item-content">${{ times.length * 5 + tips }}</div>
        </div>
        <div class="list-item" v-if="times.length !== 0 && feedback">
          <div class="item-title">Customer Feedback:</div>
          <div class="item-content">{{ feedback }}</div>
        </div>
      </div>
      <div class="buttons-container">
        <button class="accept-button" @click="onAccept">Continue</button>
      </div>
    </div>
  </div>
</template>

<script>
import gameStateKeys from "@/assets/gameStateKeys";

export default {
  name: "EndOfDayDialog",
  props: {
    times: Array,
    visible: Boolean,
    gameStateKey: String,
  },
  data() {
    return {
      internalVisible: this.visible,
      shown: this.visible,
      feedbacks: undefined,
    };
  },
  computed: {
    totalTime() {
      return this.times.reduce((acc, val) => acc + val, 0);
    },
    starsCount() {
      const numOfStars = (this.tips / (this.times.length * 3)) * 5;

      return Math.min(Math.round(numOfStars), 5);
    },
    stars() {
      const numOfStars = this.starsCount;
      const stars = [];

      for (let i = 0; i < numOfStars; i++) stars.push(true);
      for (let i = 0; i < 5 - numOfStars; i++) stars.push(false);

      return stars;
    },
    tips() {
      return this.times.reduce(
        (acc, val) =>
          acc +
          (val <= 10 ? 3 : Math.min(3 / (Math.log(val - 10) / Math.log(5)), 3)),
        0
      );
    },
    feedback() {
      if (this.feedbacks && this.feedbacks[this.gameStateKey]) {
        const listOfFeedback = this.feedbacks[this.gameStateKey][
          this.starsCount.toString()
        ];

        return listOfFeedback[
          Math.floor(Math.random() * listOfFeedback.length)
        ];
      } else return undefined;
    },
  },
  methods: {
    onAccept() {
      this.internalVisible = false;

      setTimeout(() => {
        this.shown = false;
        this.$emit("accept");

        this.$nextTick(() => this.syncVisible(this.visible));
      }, 250);
    },
    syncVisible(value) {
      if (value) {
        this.shown = true;
        setTimeout(() => {
          this.internalVisible = true;
        }, 50);
      } else {
        this.internalVisible = false;
        setTimeout(() => {
          this.shown = false;
        }, 250);
      }
    },
  },
  watch: {
    visible(value) {
      this.syncVisible(value);
    },
  },
  async mounted() {
    const feedbackJsons = await Promise.all(
      (
        await Promise.all(
          gameStateKeys.map(key =>
            Promise.all([
              fetch(`/game-content/${key}/drone/feedback.json`),
              Promise.resolve(key),
            ])
          )
        )
      )
        .filter(([response]) => response.ok)
        .map(([response, key]) =>
          Promise.all([response.json(), Promise.resolve(key)])
        )
    );

    this.feedbacks = {};

    feedbackJsons.map(([value, key]) => {
      this.feedbacks[key] = value;
    });
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables";

.overlay {
  background-color: rgba(black, 0.5);

  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;

  z-index: 10;

  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  transition-property: opacity;
  transition-duration: 250ms;
  transition-timing-function: ease-in-out;

  opacity: 0;

  &.shown {
    display: flex;
  }

  &.visible {
    opacity: 1;

    .dialog {
      transform: none;
    }
  }

  .dialog {
    width: 300px;

    padding: 16px;

    border-radius: 6px;

    background-color: $dialog-background;
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);

    transition-property: transform;
    transition-duration: 250ms;
    transition-timing-function: ease-in-out;

    transform: scale(0);

    .title {
      color: $primary-text-white;
    }

    .content {
      width: 100%;

      .list-item {
        margin-top: 8px;
        margin-bottom: 8px;

        .item-title {
          font-size: 0.8rem;
          font-weight: bold;
          color: $secondary-text-white;
        }

        .item-content {
          color: $primary-text-white;

          &.rating {
            font-size: 1rem;
            color: gold;
          }
        }
      }
    }

    .buttons-container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      margin-top: 32px;

      button {
        background: none;
        border: none;
        padding: 0px;
        color: $primary-text-white;
        user-select: none;
        cursor: pointer;
      }

      .accept-button {
        background-color: #00c853;
        text-transform: uppercase;
        padding: 16px;
        width: 100%;
        border-radius: 6px;

        transition-property: background-color;
        transition-duration: 100ms;
        transition-timing-function: ease-in-out;

        font-size: 1rem;

        &:hover {
          background-color: mix(#00c853, black, 90%);
        }

        &:active {
          background-color: mix(#00c853, black, 80%);
        }
      }
    }
  }
}
</style>
